/**
 * Widget palette component.
 * Displays a curated set of widget templates that can be added to a dashboard view.
 */

import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';

/**
 * Describes an addable dashboard widget template.
 */
export interface WidgetTemplate {
  /**
   * Widget type identifier persisted in the backend (e.g., MetricCard, Chart).
   */
  widgetType: string;

  /**
   * Default title for the widget.
   */
  title: string;

  /**
   * Optional description for the widget.
   */
  description?: string;

  /**
   * Default grid width.
   */
  defaultWidth: number;

  /**
   * Default grid height.
   */
  defaultHeight: number;

  /**
   * Widget configuration JSON persisted in the backend.
   */
  configurationJson: string;
}

const defaultTemplates: WidgetTemplate[] = [
  {
    widgetType: 'MetricCard',
    title: 'Metric Card',
    description: 'A quick KPI card (configure which metric in JSON).',
    defaultWidth: 3,
    defaultHeight: 2,
    configurationJson: JSON.stringify({ metric: 'activeSessions' }),
  },
  {
    widgetType: 'UsageTrends',
    title: 'Usage Trends',
    description: 'Tokens and cost trend chart for the selected range.',
    defaultWidth: 8,
    defaultHeight: 6,
    configurationJson: JSON.stringify({ rangeDays: 30 }),
  },
  {
    widgetType: 'CostByModel',
    title: 'Cost by Model',
    description: 'Pie chart of cost split by model.',
    defaultWidth: 4,
    defaultHeight: 6,
    configurationJson: JSON.stringify({ topN: 6 }),
  },
  {
    widgetType: 'RealTimeMetrics',
    title: 'Real-time Activity',
    description: 'Live activity widget backed by SignalR.',
    defaultWidth: 4,
    defaultHeight: 5,
    configurationJson: JSON.stringify({}),
  },
];

export interface WidgetPaletteProps {
  /**
   * Callback invoked when the user adds a widget template.
   */
  onAdd: (template: WidgetTemplate) => void;
}

/**
 * Widget palette component.
 */
export const WidgetPalette = ({ onAdd }: WidgetPaletteProps) => {
  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Widget Palette
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add widgets, then drag/resize in the canvas to arrange your dashboard.
      </Typography>

      <Grid container spacing={2}>
        {defaultTemplates.map((t) => (
          <Grid key={t.widgetType} size={{ xs: 12, sm: 6, md: 12 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t.title}
                </Typography>
                {t.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t.description}
                  </Typography>
                )}
                <Button variant="contained" size="small" onClick={() => onAdd(t)}>
                  Add
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

